import type {
  CI,
  CIActions,
  CIActionsOnly,
  CICreate,
  CICreateMap,
  CIEssential,
  CIExtended,
  CIExtendedMap,
  CILink,
  CIResolved,
  CIUpdate,
  CIUpdateMap,
} from '@/entities/srm/ci';

import type {
  CILoadAttributesRequest,
  CILoadAttributesResponse,
} from '@/entities/srm/ci';

import type { AxiosRequestConfig } from 'axios';

import { Page } from '@/shared/api/pagination';

import type { TQLFilter } from '@/entities/srm/tql-query/@x/ci';

import { srmInstance } from '@/shared/api/api-instances/srm/api-instance';

// TODO: Заменить в коде и апи на простое прокидывание ID
// interface changeRequestParam {
//   change_request_id: string | undefined;
// }

export interface CisOptions {
  add_name: boolean;
  add_attribute_values: boolean;
}

export interface ConnectedCIsResponse {
  nodes: CI[];
  links: CILink[];
}

const baseURL = '/api/srm/v4/';
const DEFAULT_CIS_OPTIONS: CisOptions = {
  add_name: true,
  add_attribute_values: false,
};
const defaultOptions = { baseURL };

export class CISApi {
  static getCIWithActions(id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<CIEssential>(`/ci/with_actions/${id}`, {
      ...options,
      baseURL,
    });
  }

  static getCIsWithActionsOnly(ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<CIActionsOnly[]>(`/cis/with_actions`, ids, {
      ...options,
      baseURL,
    });
  }

  static getCIActions(ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<(CIActions & { ci_id: string })[]>(
      `/cis/with_actions`,
      ids,
      {
        ...options,
        baseURL,
      },
    );
  }

  static getCI(ci_id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<CIExtended>(`/ci/${ci_id}`, {
      ...defaultOptions,
      ...options,
    });
  }

  static getCIMap(ci_id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<CIExtendedMap>(`/ci/${ci_id}/map`, {
      ...defaultOptions,
      ...options,
    });
  }

  static getCIName(ci_id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<string>(`/ci/${ci_id}/name`, {
      ...defaultOptions,
      ...options,
    });
  }

  static createCI(payload: CICreate, options?: AxiosRequestConfig) {
    return srmInstance.post<CI>(`/ci`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static createCIMap(payload: CICreateMap, options?: AxiosRequestConfig) {
    return srmInstance.post<CIResolved>(`/ci/map`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static updateCI(
    ci_id: string,
    payload: CIUpdate,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<CI>(`/ci/${ci_id}`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static updateCIMap(
    ci_id: string,
    payload: CIUpdateMap,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<CIResolved>(`/ci/${ci_id}/map`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static deleteCI(
    ci_id: string,
    // payload: changeRequestParam,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.delete<string[]>(`/ci/${ci_id}`, {
      ...defaultOptions,
      ...options,
    });
  }

  static getCIsByIds(
    ids: string[],
    cisOptions?: CisOptions,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<Omit<CIExtended, 'attribute_values'>[]>(
      '/cis',
      ids,
      {
        ...defaultOptions,
        ...options,
        params: {
          ...DEFAULT_CIS_OPTIONS,
          ...cisOptions,
        },
      },
    );
  }

  static getCITypeCIs(
    ci_type_name: string[],
    options: AxiosRequestConfig = {},
  ) {
    return srmInstance.get<Omit<CIExtended, 'attribute_values'>[]>(
      `/cis/ci_type_name/${ci_type_name}`,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static deleteCIs(
    ci_ids: string[],
    // change_request_id: changeRequestParam,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<string[]>('/cis/delete', ci_ids, {
      ...defaultOptions,
      ...options,
    });
  }

  static deleteMarkedCIs(options?: AxiosRequestConfig) {
    return srmInstance.delete('/cis/marked', {
      ...defaultOptions,
      ...options,
    });
  }

  static setCIFingerprint(
    ci_id: string,
    payload: { fingerprint: string },
    options: AxiosRequestConfig = {},
  ) {
    return srmInstance.put<CI>(`/ci/${ci_id}/set_fingerprint`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static getCIsAttributes(
    ci_ids: string[],
    attr_names: string[],
    options?: AxiosRequestConfig,
  ) {
    const newParams = new URLSearchParams();
    for (let i in options?.params || {}) {
      newParams.set(
        i,
        typeof options?.params[i] === 'string'
          ? options?.params[i]
          : JSON.stringify(options?.params[i]),
      );
    }
    newParams.set('ci_ids', JSON.stringify(ci_ids));
    newParams.set('attr_names', JSON.stringify(attr_names));
    return srmInstance.get<Page<Record<string, any>>>('/cis/attributes', {
      ...defaultOptions,
      ...options,
      params: newParams,
    });
  }

  static getCIsGraph(
    citID: string,
    attributesNames: string[],
    data,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<Page<Record<string, any>>>(
      '/cis/attributes/graph',
      {
        ui_schema: {
          query: {
            nodes: [
              {
                id: citID,
                data,
              },
            ],
            edges: [],
          },
        },
        attr_names: attributesNames,
      },
      {
        ...defaultOptions,
        ...options,
        params: options?.params || {},
      },
    );
  }

  static getCIsAttributesPOST(
    ci_ids: string[],
    attr_names: string[],
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<Page<Record<string, any>>>(
      '/cis/attributes',
      { ci_ids, attr_names },
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static getCISCoordinates(ci_ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<Record<string, number[]>>(
      `/cis/coordinates`,
      ci_ids,
      {
        ...options,
        baseURL,
      },
    );
  }

  static getCIsMap(ci_ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<Record<string, CI>>('/cis/map', ci_ids, {
      ...defaultOptions,
      ...options,
    });
  }

  static exportCIsCSVFile(
    payload?: { ci_ids: string[]; attr_names: string[] },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post(`/export/cis/csv_file`, payload, options);
  }

  static exportCIsCSV(
    payload?: { ci_ids: string[]; attr_names: string[] },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post(`/export/cis/csv`, payload, options);
  }

  static exportCIsJSONFile(
    payload?: {
      query: TQLFilter;
      node_attrs: Record<string, Record<string, string[]>>;
      include?: string[];
    },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post(`/export/cis/json_graph_file`, payload, options);
  }

  static exportCIsJSON(
    payload?: {
      query: TQLFilter;
      node_attrs: Record<string, Record<string, string[]>>;
      include?: string[];
    },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post(`/export/cis/json_graph`, payload, options);
  }

  // static getCitCiCount(ci_id: string, options: AxiosRequestConfig = {}) {
  //   return srmInstance.get<CiExtendedDTO>(`/srm/ci/${ci_id}`, options);
  // }
  // static getCiWithEnrich(ci_id: string, options: AxiosRequestConfig = {}) {
  //   return srmInstance.get<CIEnrichSchema>(`/srm/ci_with_enrich/${ci_id}`, options);
  // }

  static loadCIattributes(
    payload: CILoadAttributesRequest,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<CILoadAttributesResponse>(
      '/ci/load_attributes',
      payload,
      options,
    );
  }

  // static getCacheGraphById(query_id: string, options?: AxiosRequestConfig) {
  //   return srmInstance.get<CIGraphResponse>(`/srm/cached_graph/${query_id}`, options);
  // }

  static deployConfig(
    ci_id: string,
    config_id: string,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<string>(
      `/ci/${ci_id}/deploy_config/${config_id}`,
      options,
    );
  }

  static executeCIEnrichment(
    ci_id: string,
    payload?: {
      attributes: boolean;
      links: { link_name: string; dst_ci_type_name: string }[];
    },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post(`/ci/enrichment/${ci_id}`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static executeCIsEnrichment(payload: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<string>(`/ci/enrichment`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static getReferencedNodes(ci_id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<ConnectedCIsResponse>(`/ci/${ci_id}/connected`, {
      ...defaultOptions,
      ...options,
    });
  }

  // // TODO: В Swagger нет типов для данного action
  // static resolveCiConflict(payload: any, options?: AxiosRequestConfig) {
  //   return srmInstance.post<ResolveData>(`/resolve_conflict`, payload, options);
  // }

  // API CI import
  static getCICSVTemplate(ci_type_name: string) {
    return srmInstance.get(`/import/cis/csv/${ci_type_name}/template`, {
      responseType: 'blob',
      ...defaultOptions,
    });
    // return srmInstance.get(`/srm/import/csv_template/${id}`);
  }

  static importCIFromCSV(ci_type_name, file) {
    return srmInstance.post(`/import/cis/csv/${ci_type_name}`, file, {
      ...defaultOptions,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static importGraphFromJSON(file) {
    return srmInstance.post(`/import/cis/json`, file, {
      ...defaultOptions,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}